import { Injectable } from '@angular/core';
import { ComponentBase } from '../../../component-base';
import { IKeyDown, ICloneable, IObject3D } from '../Interfaces';
import { DrawingsHolderService } from '../drawings-holder.service';
import { PositionCalculatorService } from '../position-calculator.service';
import { ToasterService } from 'angular2-toaster';
import { common } from '../common';
import { Edge, IPost, Post, SelectableIntermediatePost, SelectablePicket } from '../object3D';
import { ObbBuilder } from '../../collision/obb-builder.service';
import { PostFactoryService } from '../post-factory.service';

declare const THREE: any;

@Injectable()
export class CloneObjectComponent extends ComponentBase implements IKeyDown {
  private selectedObject: ICloneable | undefined;
  private position = new THREE.Vector3();
  private copy: IObject3D;
  private copyType: string;
  private point = new THREE.Vector3();

  private raycaster = new THREE.Raycaster(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, -1, 0));

  constructor(
    private drawings: DrawingsHolderService,
    private positionCalculator: PositionCalculatorService,
    private toasterService: ToasterService,
    private postFactory: PostFactoryService
  ) {
    super();
    this.drawings.onSelect.subscribe(obj => {
      if (obj) {
        this.selectedObject = obj[0];
      } else {
        this.selectedObject = null;
      }
    });
  }

  private ctrlC(e: KeyboardEvent): boolean {
    const isCKey = e.keyCode === 67;
    if (this.selectedObject && e.ctrlKey && isCKey) {
      if (this.selectedObject instanceof SelectableIntermediatePost) {
        this.copyType = 'SelectableIntermediatePost';
      } else if (this.selectedObject instanceof SelectablePicket) {
        this.copyType = 'SelectablePicket';
      } else {
        this.copy = this.selectedObject.duplicate();
        this.copyType = 'DrawingElement';
      }
      this.toasterService.pop({
        timeout: 1500,
        type: 'success',
        body: 'Copied'
      });
      return true;
    }
    return false;
  }

  private ctrlV(e: KeyboardEvent) {
    const isVKey = e.keyCode === 86;
    if (this.copy && e.ctrlKey && isVKey) {
      this.copy.position.copy(this.position).setY(0);
      this.drawings.canvas.add(this.copy);
      this.copy = null;
      this.copyType = null;
      return true;
    } else if (this.copyType === 'SelectableIntermediatePost' && e.ctrlKey && isVKey) {
      if (this.duplicatePost()) {
        this.copyType = null;
        return true;
      }
      return false;
    } else if (this.copyType === 'SelectablePicket' && e.ctrlKey && isVKey) {
      if (this.duplicatePicket()) {
        this.copyType = null;
        return true;
      }
      return false;
    }
    return false;
  }

  keyDown(e: KeyboardEvent): boolean {
    return this.ctrlC(e) || this.ctrlV(e) || false;
  }

  mouseMove(e: MouseEvent) {
    const position = this.positionCalculator.calculate(e);
    this.position.copy(position);

    const point = this.positionCalculator.calculate(e).setY(common.raycastAtY);
    this.point = point;
    return false;
  }

  private canPlace(post: IPost): boolean {
    const posts = this.drawings.canvas.children.filter(
      ch => ch instanceof SelectableIntermediatePost || ch instanceof Post
    );

    const postsOBB = posts.map(p =>
      new ObbBuilder()
        .setPosition(p.position.clone().setY(0))
        .setSize(new THREE.Vector3(p.getSize(), 0, p.getSize()))
        .build(p)
    );

    const addedPostOBB = new ObbBuilder()
      .setSize(new THREE.Vector3(post.getSize(), 0, post.getSize()))
      .build(post);

    return postsOBB.find(obb => obb.isIntersectionOBB(addedPostOBB)) === undefined;
  }

  private duplicatePost(): boolean {
    this.raycaster.ray.origin.copy(this.point);

    const edges = this.drawings.canvas.children.filter(ch => ch instanceof Edge);

    const intersection = this.raycaster.intersectObjects(edges);

    if (intersection.length !== 0) {
      const edgeMesh = intersection[0].object as Edge;

      const edge = edgeMesh.edge;
      const projected = this.point.setY(0).sub(edge.start).projectOnVector(edge.direction);

      const locked = edge.start.add(projected);

      const postInfo = Object.assign(locked, {
        corner: false,
        isTerminationPost: false,
        rotation: edge.rotationAngle
      });

      const post = this.postFactory.create(postInfo, edge);
      post.position.copy(locked).setY(0);

      if (this.canPlace(post)) {
        this.drawings.canvas.add(post);
      }

      return true;
    }

    return false;
  }

  private duplicatePicket(): boolean {
    this.raycaster.ray.origin.copy(this.point);

    const edges = this.drawings.canvas.children.filter(ch => ch instanceof Edge);

    const intersection = this.raycaster.intersectObjects(edges);

    if (intersection.length !== 0) {
      const edgeMesh = intersection[0].object as Edge;

      const edge = edgeMesh.edge;
      const projected = this.point.setY(0).sub(edge.start).projectOnVector(edge.direction);

      const locked = edge.start.add(projected);

      const postInfo = Object.assign(locked);

      const post = this.postFactory.createPicket(postInfo, edge);
      post.position.copy(locked).setY(0);

      if (this.canPlace(post)) {
        this.drawings.canvas.add(post);
      }

      return true;
    }

    return false;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/proTools/components/CloneObjectComponent.ts